/**
 * The office ribbon icon set (I-2066): one drawing per command, shared by Calc
 * and Write so the same command looks the same in both.
 *
 * Every icon is a 24-unit Lucide-style line drawing. A node is
 * `[tag, attributes, stroke role?, fill role?]`; the stroke role defaults to
 * `ink`. Roles are colours in the Excel manner — dark outline, white sheets,
 * colour as a fill on the part that carries the meaning — and resolve to
 * `--nxt-*` tokens in `office-icon.css`; no colour is written here. Filled
 * nodes come first so a sheet or tint fill never covers a line.
 *
 * The formatting letters (B, U, S, A, Aa, grow/shrink font) are not drawings:
 * NX_OFFICE_LETTERS renders them as HTML text of the UI font at a whole-pixel
 * token size. Text inside a scaled SVG loses the font's hinting and reads soft
 * at 16 px; plain text is rasterised like every other label.
 *
 * Most drawings are taken from Lucide 0.577.0 (https://lucide.dev, noted as
 * `lucide <name>` above each entry; its licence follows below); the ones marked
 * `own` were drawn for this set where Lucide has no fitting glyph (chart types,
 * pivot, slicer, sparklines, borders, formula auditing, …).
 */

/*! @license Lucide (https://lucide.dev), the drawings marked `lucide <name>`:
 *
 * ISC License
 *
 * Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2026 as part of Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2026.
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 *
 * ---
 *
 * The MIT License (MIT) (for portions derived from Feather)
 *
 * Copyright (c) 2013-2026 Cole Bemis
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export type NxOfficeIconRole =
  | 'ink'
  | 'muted'
  | 'paper'
  | 'gray'
  | 'blue'
  | 'blue-tint'
  | 'green'
  | 'green-tint'
  | 'yellow'
  | 'yellow-line'
  | 'yellow-tint'
  | 'orange'
  | 'red'
  | 'red-tint'
  | 'none'
  | 'fill-current'
  | 'calc'
  | 'calc-on'
  | 'write'
  | 'write-on';

export type NxOfficeIconNode = readonly [
  tag: 'path' | 'rect' | 'circle' | 'line',
  attrs: Readonly<Record<string, string>>,
  stroke?: NxOfficeIconRole,
  fill?: NxOfficeIconRole,
];

export const NX_OFFICE_ICONS = {
  // lucide file-plus
  new: [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M9 15h6' }, 'green'],
    ['path', { d: 'M12 18v-6' }, 'green'],
  ],
  // lucide folder-open
  open: [
    [
      'path',
      {
        d: 'm6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2',
      },
      'yellow-line',
      'yellow',
    ],
  ],
  // lucide file-spreadsheet
  'recent-workbook': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M8 13h2' }, 'green'],
    ['path', { d: 'M14 13h2' }, 'green'],
    ['path', { d: 'M8 17h2' }, 'green'],
    ['path', { d: 'M14 17h2' }, 'green'],
  ],
  // lucide save
  save: [
    [
      'path',
      {
        d: 'M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7' }, 'blue', 'paper'],
    ['path', { d: 'M7 3v4a1 1 0 0 0 1 1h7' }, 'blue'],
  ],
  // lucide save-all
  'save-as': [
    [
      'path',
      {
        d: 'M8 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9.172a2 2 0 0 1 1.414.586l2.828 2.828A2 2 0 0 1 22 6.828V16a2 2 0 0 1-2.01 2z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M18 18v-6a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6' }, 'blue', 'paper'],
    ['path', { d: 'M10 2v3a1 1 0 0 0 1 1h5' }, 'blue'],
    ['path', { d: 'M18 22H4a2 2 0 0 1-2-2V6' }],
  ],
  // lucide share-2
  share: [
    ['circle', { cx: '18', cy: '5', r: '3' }, 'blue', 'blue-tint'],
    ['circle', { cx: '6', cy: '12', r: '3' }, 'blue', 'blue-tint'],
    ['circle', { cx: '18', cy: '19', r: '3' }, 'blue', 'blue-tint'],
    ['line', { x1: '8.59', x2: '15.42', y1: '13.51', y2: '17.49' }, 'blue', 'blue-tint'],
    ['line', { x1: '15.41', x2: '8.59', y1: '6.51', y2: '10.49' }, 'blue', 'blue-tint'],
  ],
  // lucide file-lock
  encrypt: [
    [
      'path',
      {
        d: 'M4 9.8V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2h-3',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['rect', { width: '8', height: '5', x: '3', y: '17', rx: '1' }, 'yellow-line', 'yellow'],
    ['path', { d: 'M9 17v-2a2 2 0 0 0-4 0v2' }, 'yellow-line'],
  ],
  // lucide file-down
  download: [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M12 18v-6' }, 'blue'],
    ['path', { d: 'm9 15 3 3 3-3' }, 'blue'],
  ],
  // lucide file-down
  'download-xlsx': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M12 18v-6' }, 'green'],
    ['path', { d: 'm9 15 3 3 3-3' }, 'green'],
  ],
  // lucide printer
  print: [
    [
      'path',
      { d: 'M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2' },
      'ink',
      'gray',
    ],
    ['path', { d: 'M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6' }, 'ink', 'paper'],
    ['rect', { x: '6', y: '14', width: '12', height: '8', rx: '1' }, 'ink', 'paper'],
  ],
  // lucide square-dashed
  'print-area-set': [
    ['path', { d: 'M5 3a2 2 0 0 0-2 2' }, 'blue'],
    ['path', { d: 'M19 3a2 2 0 0 1 2 2' }, 'blue'],
    ['path', { d: 'M21 19a2 2 0 0 1-2 2' }, 'blue'],
    ['path', { d: 'M5 21a2 2 0 0 1-2-2' }, 'blue'],
    ['path', { d: 'M9 3h1' }, 'blue'],
    ['path', { d: 'M9 21h1' }, 'blue'],
    ['path', { d: 'M14 3h1' }, 'blue'],
    ['path', { d: 'M14 21h1' }, 'blue'],
    ['path', { d: 'M3 9v1' }, 'blue'],
    ['path', { d: 'M21 9v1' }, 'blue'],
    ['path', { d: 'M3 14v1' }, 'blue'],
    ['path', { d: 'M21 14v1' }, 'blue'],
  ],
  // lucide square-x
  'print-area-clear': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2', ry: '2' }, 'ink', 'paper'],
    ['path', { d: 'm15 9-6 6' }, 'red'],
    ['path', { d: 'm9 9 6 6' }, 'red'],
  ],
  // lucide scissors
  cut: [
    ['circle', { cx: '6', cy: '6', r: '3' }, 'blue'],
    ['path', { d: 'M8.12 8.12 12 12' }],
    ['path', { d: 'M20 4 8.12 15.88' }],
    ['circle', { cx: '6', cy: '18', r: '3' }, 'blue'],
    ['path', { d: 'M14.8 14.8 20 20' }],
  ],
  // lucide copy
  copy: [
    ['rect', { width: '14', height: '14', x: '8', y: '8', rx: '2', ry: '2' }, 'ink', 'paper'],
    ['path', { d: 'M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' }, 'muted'],
  ],
  // lucide clipboard-paste
  paste: [
    [
      'path',
      { d: 'M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 1.793-1.113' },
      'orange',
      'yellow-tint',
    ],
    ['rect', { x: '8', y: '2', width: '8', height: '4', rx: '1' }, 'ink', 'gray'],
    ['path', { d: 'M11 14h10' }, 'blue'],
    ['path', { d: 'M16 4h2a2 2 0 0 1 2 2v1.344' }, 'orange'],
    ['path', { d: 'm17 18 4-4-4-4' }, 'blue'],
  ],
  // lucide clipboard-list
  'paste-special': [
    [
      'path',
      { d: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' },
      'orange',
      'yellow-tint',
    ],
    ['rect', { width: '8', height: '4', x: '8', y: '2', rx: '1', ry: '1' }, 'ink', 'gray'],
    ['path', { d: 'M12 11h4' }],
    ['path', { d: 'M12 16h4' }],
    ['path', { d: 'M8 11h.01' }, 'blue'],
    ['path', { d: 'M8 16h.01' }, 'blue'],
  ],
  // letter of the UI font
  italic: [
    ['rect', { x: '10.5', y: '3', width: '9', height: '1.5' }, 'none', 'ink'],
    ['rect', { x: '4.5', y: '19.5', width: '9', height: '1.5' }, 'none', 'ink'],
    ['line', { x1: '15', y1: '4.5', x2: '9', y2: '19.5', 'stroke-width': '2' }],
  ],
  // own, bars on the pixel grid
  'fill-color': [
    [
      'path',
      {
        d: 'm19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z',
        'stroke-width': '1.875',
        transform: 'translate(1.2 0) scale(0.8)',
      },
      'ink',
      'paper',
    ],
    ['rect', { x: '3', y: '19.5', width: '18', height: '3' }, 'none', 'fill-current'],
    ['path', { d: 'm5 2 5 5', 'stroke-width': '1.875', transform: 'translate(1.2 0) scale(0.8)' }],
    ['path', { d: 'M2 13h15', 'stroke-width': '1.875', transform: 'translate(1.2 0) scale(0.8)' }],
  ],
  // lucide text-align-start
  'align-left': [
    ['path', { d: 'M21 5H3' }],
    ['path', { d: 'M15 12H3' }],
    ['path', { d: 'M17 19H3' }],
  ],
  // lucide text-align-center
  'align-center': [
    ['path', { d: 'M21 5H3' }],
    ['path', { d: 'M17 12H7' }],
    ['path', { d: 'M19 19H5' }],
  ],
  // lucide text-align-end
  'align-right': [
    ['path', { d: 'M21 5H3' }],
    ['path', { d: 'M21 12H9' }],
    ['path', { d: 'M21 19H7' }],
  ],
  // lucide text-wrap
  'wrap-text': [
    ['path', { d: 'm16 16-3 3 3 3' }, 'blue'],
    ['path', { d: 'M3 12h14.5a1 1 0 0 1 0 7H13' }, 'blue'],
    ['path', { d: 'M3 19h6' }],
    ['path', { d: 'M3 5h18' }],
  ],
  // lucide table-cells-merge
  'merge-cells': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M12 21v-6' }, 'blue'],
    ['path', { d: 'M12 9V3' }, 'blue'],
    ['path', { d: 'M3 15h18' }, 'muted'],
    ['path', { d: 'M3 9h18' }, 'muted'],
  ],
  // lucide table-cells-split
  'split-cells': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M12 15V9' }, 'blue'],
    ['path', { d: 'M3 15h18' }, 'muted'],
    ['path', { d: 'M3 9h18' }, 'muted'],
  ],
  // lucide table-properties
  'format-cells': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M15 3v18' }, 'muted'],
    ['path', { d: 'M21 9H3' }, 'blue'],
    ['path', { d: 'M21 15H3' }, 'muted'],
  ],
  // lucide grid-3x3
  'borders-all': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M3 9h18' }],
    ['path', { d: 'M3 15h18' }],
    ['path', { d: 'M9 3v18' }],
    ['path', { d: 'M15 3v18' }],
  ],
  // own
  'borders-outer': [
    ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '.5' }, 'ink', 'paper'],
    ['path', { d: 'M3 12h18M12 3v18', 'stroke-dasharray': '1.5 2.2' }, 'muted'],
  ],
  // own
  'borders-none': [
    [
      'rect',
      { x: '3', y: '3', width: '18', height: '18', rx: '.5', 'stroke-dasharray': '1.5 2.2' },
      'muted',
      'paper',
    ],
    ['path', { d: 'M3 12h18M12 3v18', 'stroke-dasharray': '1.5 2.2' }, 'muted'],
  ],
  // lucide paintbrush
  'format-painter': [
    [
      'path',
      {
        d: 'M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15',
      },
      'yellow-line',
      'yellow',
    ],
    ['path', { d: 'm14.622 17.897-10.68-2.913' }],
    [
      'path',
      {
        d: 'M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z',
      },
    ],
  ],
  // lucide fold-horizontal
  'hide-columns': [
    ['path', { d: 'M2 12h6' }, 'blue'],
    ['path', { d: 'M22 12h-6' }, 'blue'],
    ['path', { d: 'M12 2v2' }, 'muted'],
    ['path', { d: 'M12 8v2' }, 'muted'],
    ['path', { d: 'M12 14v2' }, 'muted'],
    ['path', { d: 'M12 20v2' }, 'muted'],
    ['path', { d: 'm19 9-3 3 3 3' }, 'blue'],
    ['path', { d: 'm5 15 3-3-3-3' }, 'blue'],
  ],
  // lucide unfold-horizontal
  'unhide-columns': [
    ['path', { d: 'M16 12h6' }, 'blue'],
    ['path', { d: 'M8 12H2' }, 'blue'],
    ['path', { d: 'M12 2v2' }, 'muted'],
    ['path', { d: 'M12 8v2' }, 'muted'],
    ['path', { d: 'M12 14v2' }, 'muted'],
    ['path', { d: 'M12 20v2' }, 'muted'],
    ['path', { d: 'm19 15 3-3-3-3' }, 'blue'],
    ['path', { d: 'm5 9-3 3 3 3' }, 'blue'],
  ],
  // lucide fold-vertical
  'hide-rows': [
    ['path', { d: 'M12 22v-6' }, 'blue'],
    ['path', { d: 'M12 8V2' }, 'blue'],
    ['path', { d: 'M4 12H2' }, 'muted'],
    ['path', { d: 'M10 12H8' }, 'muted'],
    ['path', { d: 'M16 12h-2' }, 'muted'],
    ['path', { d: 'M22 12h-2' }, 'muted'],
    ['path', { d: 'm15 19-3-3-3 3' }, 'blue'],
    ['path', { d: 'm15 5-3 3-3-3' }, 'blue'],
  ],
  // lucide unfold-vertical
  'unhide-rows': [
    ['path', { d: 'M12 22v-6' }, 'blue'],
    ['path', { d: 'M12 8V2' }, 'blue'],
    ['path', { d: 'M4 12H2' }, 'muted'],
    ['path', { d: 'M10 12H8' }, 'muted'],
    ['path', { d: 'M16 12h-2' }, 'muted'],
    ['path', { d: 'M22 12h-2' }, 'muted'],
    ['path', { d: 'm15 19-3 3-3-3' }, 'blue'],
    ['path', { d: 'm15 5-3-3-3 3' }, 'blue'],
  ],
  // own: a column between its two borders, the width as a double arrow
  'column-width': [
    ['path', { d: 'M3 3v18' }, 'muted'],
    ['path', { d: 'M21 3v18' }, 'muted'],
    ['path', { d: 'M7 12h10' }, 'blue'],
    ['path', { d: 'm10 9-3 3 3 3' }, 'blue'],
    ['path', { d: 'm14 9 3 3-3 3' }, 'blue'],
  ],
  // own: the row analog of column-width
  'row-height': [
    ['path', { d: 'M3 3h18' }, 'muted'],
    ['path', { d: 'M3 21h18' }, 'muted'],
    ['path', { d: 'M12 7v10' }, 'blue'],
    ['path', { d: 'm9 10 3-3 3 3' }, 'blue'],
    ['path', { d: 'm9 14 3 3 3-3' }, 'blue'],
  ],
  // own: a line of text, the arrows reaching out to the borders it fits
  'autofit-column-width': [
    ['path', { d: 'M3 3v18' }, 'muted'],
    ['path', { d: 'M21 3v18' }, 'muted'],
    ['path', { d: 'M7 7h10' }],
    ['path', { d: 'M6 16h12' }, 'blue'],
    ['path', { d: 'm9 13-3 3 3 3' }, 'blue'],
    ['path', { d: 'm15 13 3 3-3 3' }, 'blue'],
  ],
  // own: the row analog of autofit-column-width
  'autofit-row-height': [
    ['path', { d: 'M3 3h18' }, 'muted'],
    ['path', { d: 'M3 21h18' }, 'muted'],
    ['path', { d: 'M5 12h7' }],
    ['path', { d: 'M17 6v12' }, 'blue'],
    ['path', { d: 'm14 9 3-3 3 3' }, 'blue'],
    ['path', { d: 'm14 15 3 3 3-3' }, 'blue'],
  ],
  // lucide panels-top-left
  'freeze-panes': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M3 9h18' }, 'blue'],
    ['path', { d: 'M9 21V9' }, 'blue'],
  ],
  // own
  table: [
    ['rect', { x: '3', y: '4', width: '18', height: '16', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '3', y: '4', width: '18', height: '5', rx: '1.5' }, 'green', 'green'],
    ['path', { d: 'M3 14.5h18M9 9v11M15 9v11' }, 'muted'],
  ],
  // lucide text-cursor-input
  form: [
    ['path', { d: 'M12 20h-1a2 2 0 0 1-2-2 2 2 0 0 1-2 2H6' }],
    ['path', { d: 'M13 8h7a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-7' }],
    ['path', { d: 'M5 16H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h1' }],
    ['path', { d: 'M6 4h1a2 2 0 0 1 2 2 2 2 0 0 1 2-2h1' }],
    ['path', { d: 'M9 6v12' }, 'blue'],
  ],
  // own
  'pivot-table': [
    ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '1.5' }, 'ink', 'paper'],
    ['path', { d: 'M3 8h18' }],
    ['path', { d: 'M8 3v18' }],
    ['path', { d: 'M8 13h13M8 17h13M13 8v13M17 8v13' }, 'muted'],
    ['path', { d: 'M4.5 5.5h2' }, 'green'],
  ],
  // own
  slicer: [
    ['rect', { x: '4', y: '2.5', width: '16', height: '19', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '7', y: '6', width: '10', height: '3', rx: '.75' }, 'none', 'blue'],
    ['rect', { x: '7', y: '11', width: '10', height: '3', rx: '.75' }, 'muted', 'paper'],
    ['rect', { x: '7', y: '16', width: '10', height: '3', rx: '.75' }, 'muted', 'paper'],
  ],
  // lucide calendar-range
  timeline: [
    ['rect', { width: '18', height: '18', x: '3', y: '4', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M16 2v4' }],
    ['path', { d: 'M3 10h18' }],
    ['path', { d: 'M8 2v4' }],
    ['path', { d: 'M17 14h-6' }, 'blue'],
    ['path', { d: 'M13 18H7' }, 'blue'],
    ['path', { d: 'M7 14h.01' }, 'blue'],
    ['path', { d: 'M17 18h.01' }, 'blue'],
  ],
  // lucide image-plus
  picture: [
    [
      'path',
      { d: 'M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5' },
      'ink',
      'paper',
    ],
    ['circle', { cx: '9', cy: '9', r: '2' }, 'yellow-line', 'yellow'],
    ['path', { d: 'M16 5h6' }, 'blue'],
    ['path', { d: 'M19 2v6' }, 'blue'],
    ['path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' }, 'green'],
  ],
  // lucide shapes
  shapes: [
    [
      'path',
      {
        d: 'M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z',
      },
      'orange',
      'yellow-tint',
    ],
    ['rect', { x: '3', y: '14', width: '7', height: '7', rx: '1' }, 'blue', 'blue-tint'],
    ['circle', { cx: '17.5', cy: '17.5', r: '3.5' }, 'green', 'green-tint'],
  ],
  // lucide square-check
  checkbox: [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'm9 12 2 2 4-4' }, 'green'],
  ],
  // own
  'chart-recommended': [
    ['rect', { x: '6', y: '12', width: '3', height: '6', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '10.5', y: '9', width: '3', height: '9', rx: '.5' }, 'none', 'orange'],
    ['rect', { x: '15', y: '13', width: '3', height: '5', rx: '.5' }, 'none', 'green'],
    [
      'path',
      { d: 'm18.5 2 1 2.1 2.3.3-1.7 1.6.4 2.3-2-1.1-2 1.1.4-2.3-1.7-1.6 2.3-.3z' },
      'yellow-line',
      'yellow',
    ],
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
  ],
  // own
  chart: [
    ['rect', { x: '6.5', y: '11', width: '3', height: '7', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '11.5', y: '6', width: '3', height: '12', rx: '.5' }, 'none', 'orange'],
    ['rect', { x: '16.5', y: '9', width: '3', height: '9', rx: '.5' }, 'none', 'green'],
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
  ],
  // own
  'chart-column': [
    ['rect', { x: '6.5', y: '11', width: '3', height: '7', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '11.5', y: '6', width: '3', height: '12', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '16.5', y: '9', width: '3', height: '9', rx: '.5' }, 'none', 'blue'],
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
  ],
  // own
  'chart-bar': [
    ['rect', { x: '6', y: '5', width: '9', height: '3', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '6', y: '10.5', width: '13', height: '3', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '6', y: '16', width: '6', height: '2.5', rx: '.5' }, 'none', 'blue'],
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
  ],
  // lucide chart-line
  'chart-line': [
    ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
    ['path', { d: 'm19 9-5 5-4-4-3 3' }, 'blue'],
  ],
  // lucide chart-area
  'chart-area': [
    [
      'path',
      {
        d: 'M7 11.207a.5.5 0 0 1 .146-.353l2-2a.5.5 0 0 1 .708 0l3.292 3.292a.5.5 0 0 0 .708 0l4.292-4.292a.5.5 0 0 1 .854.353V16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
  ],
  // lucide chart-pie
  'chart-pie': [
    [
      'path',
      {
        d: 'M21 12c.552 0 1.005-.449.95-.998a10 10 0 0 0-8.953-8.951c-.55-.055-.998.398-.998.95v8a1 1 0 0 0 1 1z',
      },
      'orange',
      'yellow-tint',
    ],
    ['path', { d: 'M21.21 15.89A10 10 0 1 1 8 2.83' }, 'blue', 'blue-tint'],
  ],
  // own
  'chart-doughnut': [
    [
      'path',
      { d: 'M12 4.5a7.5 7.5 0 1 1-7.5 7.5', 'stroke-width': '3.5', 'stroke-linecap': 'butt' },
      'blue',
    ],
    [
      'path',
      { d: 'M4.5 12A7.5 7.5 0 0 1 12 4.5', 'stroke-width': '3.5', 'stroke-linecap': 'butt' },
      'orange',
    ],
  ],
  // own
  'chart-radar': [
    ['path', { d: 'M12 6.5 17.5 10l-1 5.5L12 19l-5-4 .5-5.5z' }, 'blue', 'blue-tint'],
    ['path', { d: 'M12 2.5 20.5 7.5v9L12 21.5 3.5 16.5v-9z' }, 'muted'],
  ],
  // own
  'chart-waterfall': [
    ['rect', { x: '6', y: '9', width: '3', height: '9', rx: '.5' }, 'none', 'blue'],
    ['rect', { x: '10.5', y: '5', width: '3', height: '4', rx: '.5' }, 'none', 'green'],
    ['rect', { x: '15', y: '5', width: '3', height: '6', rx: '.5' }, 'none', 'red'],
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
  ],
  // own
  'chart-histogram': [
    ['rect', { x: '6', y: '12', width: '3.5', height: '6' }, 'paper', 'blue'],
    ['rect', { x: '9.5', y: '6', width: '3.5', height: '12' }, 'paper', 'blue'],
    ['rect', { x: '13', y: '9', width: '3.5', height: '9' }, 'paper', 'blue'],
    ['rect', { x: '16.5', y: '14', width: '3.5', height: '4' }, 'paper', 'blue'],
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
  ],
  // lucide chart-candlestick
  'chart-box': [
    ['rect', { width: '4', height: '6', x: '7', y: '9', rx: '1' }, 'blue', 'blue-tint'],
    ['rect', { width: '4', height: '8', x: '15', y: '5', rx: '1' }, 'blue', 'blue-tint'],
    ['path', { d: 'M9 5v4' }, 'blue'],
    ['path', { d: 'M9 15v2' }, 'blue'],
    ['path', { d: 'M17 3v2' }, 'blue'],
    ['path', { d: 'M17 13v3' }, 'blue'],
    ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
  ],
  // lucide chart-spline
  sparklines: [
    ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
    ['path', { d: 'M7 16c.5-2 1.5-7 4-7 2 0 2 3 4 3 2.5 0 4.5-5 5-7' }, 'blue'],
  ],
  // own
  'sparkline-line': [
    ['rect', { x: '2', y: '6', width: '20', height: '12', rx: '1.5' }, 'ink', 'paper'],
    ['path', { d: 'm5 14.5 3-4 3 2 4-5 4 3.5' }, 'blue'],
  ],
  // own
  'sparkline-column': [
    ['rect', { x: '2', y: '6', width: '20', height: '12', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '5', y: '11.5', width: '2', height: '4' }, 'none', 'blue'],
    ['rect', { x: '9', y: '8.5', width: '2', height: '7' }, 'none', 'blue'],
    ['rect', { x: '13', y: '10.5', width: '2', height: '5' }, 'none', 'blue'],
    ['rect', { x: '17', y: '8', width: '2', height: '7.5' }, 'none', 'blue'],
  ],
  // own
  'sparkline-win-loss': [
    ['rect', { x: '2', y: '6', width: '20', height: '12', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '5', y: '8.5', width: '2', height: '3.5' }, 'none', 'green'],
    ['rect', { x: '9', y: '12', width: '2', height: '3.5' }, 'none', 'red'],
    ['rect', { x: '13', y: '8.5', width: '2', height: '3.5' }, 'none', 'green'],
    ['rect', { x: '17', y: '8.5', width: '2', height: '3.5' }, 'none', 'green'],
    ['path', { d: 'M4 12h16' }, 'muted'],
  ],
  // lucide omega
  symbol: [
    [
      'path',
      {
        d: 'M3 20h4.5a.5.5 0 0 0 .5-.5v-.282a.52.52 0 0 0-.247-.437 8 8 0 1 1 8.494-.001.52.52 0 0 0-.247.438v.282a.5.5 0 0 0 .5.5H21',
      },
    ],
  ],
  // lucide square-function
  function: [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2', ry: '2' }, 'ink', 'paper'],
    ['path', { d: 'M9 17c2 0 2.8-1 2.8-2.8V10c0-2 1-3.3 3.2-3' }, 'blue'],
    ['path', { d: 'M9 11.2h5.7' }, 'blue'],
  ],
  // lucide message-square-plus
  comment: [
    [
      'path',
      {
        d: 'M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M12 8v6' }, 'blue'],
    ['path', { d: 'M9 11h6' }, 'blue'],
  ],
  // lucide messages-square
  'comment-thread': [
    [
      'path',
      {
        d: 'M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z',
      },
      'blue',
      'blue-tint',
    ],
    [
      'path',
      {
        d: 'M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1',
      },
      'blue',
    ],
  ],
  // lucide message-square-more
  'comments-all': [
    [
      'path',
      {
        d: 'M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M12 11h.01' }, 'blue'],
    ['path', { d: 'M16 11h.01' }, 'blue'],
    ['path', { d: 'M8 11h.01' }, 'blue'],
  ],
  // The tracked-change family: a sheet with the orange change bar Word and
  // Write draw in the margin beside a changed line, plus what the command does
  // with the change.
  // own, after lucide file-pen-line
  'track-changes': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    [
      'path',
      {
        d: 'M14.364 13.634a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506l4.013-4.009a1 1 0 0 0-3.004-3.004z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M7 12v6' }, 'orange'],
    ['path', { d: 'M10 13h2' }],
  ],
  // own, after lucide file-check
  'change-accept': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M7 12v6' }, 'orange'],
    ['path', { d: 'm10 15 2 2 4-4' }, 'green'],
  ],
  // own, after lucide file-x
  'change-reject': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M7 12v6' }, 'orange'],
    ['path', { d: 'm15.5 12.5-5 5' }, 'red'],
    ['path', { d: 'm10.5 12.5 5 5' }, 'red'],
  ],
  // own, after lucide file-up
  'change-previous': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M7 12v6' }, 'orange'],
    ['path', { d: 'M13 12v6' }, 'blue'],
    ['path', { d: 'm16 15-3-3-3 3' }, 'blue'],
  ],
  // own, after lucide file-down
  'change-next': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M7 12v6' }, 'orange'],
    ['path', { d: 'M13 18v-6' }, 'blue'],
    ['path', { d: 'm10 15 3 3 3-3' }, 'blue'],
  ],
  // lucide spell-check
  'spell-check': [
    ['path', { d: 'm6 16 6-12 6 12' }],
    ['path', { d: 'M8 12h8' }],
    ['path', { d: 'm16 20 2 2 4-4' }, 'green'],
  ],
  // The next word under the red spelling wave, and the arrow that jumps to it.
  // own, after lucide spell-check-2
  'next-misspelling': [
    ['path', { d: 'm3 14 4.5-10 4.5 10' }],
    ['path', { d: 'M4.8 10h5.4' }],
    [
      'path',
      {
        d: 'M2 19c1.1 0 1.1-1.5 2.2-1.5s1.1 1.5 2.2 1.5 1.1-1.5 2.2-1.5 1.1 1.5 2.2 1.5 1.1-1.5 2.2-1.5',
      },
      'red',
    ],
    ['path', { d: 'M15 11h7' }, 'blue'],
    ['path', { d: 'm19 8 3 3-3 3' }, 'blue'],
  ],
  // lucide link
  link: [
    ['path', { d: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' }, 'blue'],
    ['path', { d: 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' }],
  ],
  // lucide sigma
  autosum: [
    [
      'path',
      {
        d: 'M18 7V5a1 1 0 0 0-1-1H6.5a.5.5 0 0 0-.4.8l4.5 6a2 2 0 0 1 0 2.4l-4.5 6a.5.5 0 0 0 .4.8H17a1 1 0 0 0 1-1v-2',
      },
    ],
  ],
  // own
  'trace-precedents': [
    ['rect', { x: '2', y: '3', width: '8', height: '5', rx: '1' }, 'ink', 'paper'],
    ['rect', { x: '14', y: '16', width: '8', height: '5', rx: '1' }, 'ink', 'blue-tint'],
    ['path', { d: 'M6 8v5a3 3 0 0 0 3 3h3' }, 'blue'],
    ['path', { d: 'm10.5 14 2 2-2 2' }, 'blue'],
  ],
  // own
  'trace-dependents': [
    ['rect', { x: '2', y: '3', width: '8', height: '5', rx: '1' }, 'ink', 'blue-tint'],
    ['rect', { x: '14', y: '16', width: '8', height: '5', rx: '1' }, 'ink', 'paper'],
    ['path', { d: 'M6 8v5a3 3 0 0 0 3 3h3' }, 'blue'],
    ['path', { d: 'm10.5 14 2 2-2 2' }, 'blue'],
  ],
  // lucide eraser
  erase: [
    [
      'path',
      {
        d: 'M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'm5.082 11.09 8.828 8.828' }, 'red'],
  ],
  // lucide scan-search
  'evaluate-formula': [
    ['circle', { cx: '12', cy: '12', r: '3' }, 'ink', 'blue-tint'],
    ['path', { d: 'M3 7V5a2 2 0 0 1 2-2h2' }],
    ['path', { d: 'M17 3h2a2 2 0 0 1 2 2v2' }],
    ['path', { d: 'M21 17v2a2 2 0 0 1-2 2h-2' }],
    ['path', { d: 'M7 21H5a2 2 0 0 1-2-2v-2' }],
    ['path', { d: 'm16 16-1.9-1.9' }],
  ],
  // lucide binoculars
  'watch-window': [
    [
      'path',
      {
        d: 'M20 21a2 2 0 0 0 2-2v-3.851c0-1.39-2-2.962-2-4.829V8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v11a2 2 0 0 0 2 2z',
      },
      'ink',
      'blue-tint',
    ],
    [
      'path',
      {
        d: 'M4 21a2 2 0 0 1-2-2v-3.851c0-1.39 2-2.962 2-4.829V8a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v11a2 2 0 0 1-2 2z',
      },
      'ink',
      'blue-tint',
    ],
    ['path', { d: 'M10 10h4' }],
    ['path', { d: 'M19 7V4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v3' }],
    ['path', { d: 'M 22 16 L 2 16' }],
    ['path', { d: 'M9 7V4a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v3' }],
  ],
  // lucide calculator
  'calculation-options': [
    ['rect', { width: '16', height: '20', x: '4', y: '2', rx: '2' }, 'ink', 'paper'],
    ['line', { x1: '8', x2: '16', y1: '6', y2: '6' }, 'blue'],
    ['line', { x1: '16', x2: '16', y1: '14', y2: '18' }],
    ['path', { d: 'M16 10h.01' }],
    ['path', { d: 'M12 10h.01' }],
    ['path', { d: 'M8 10h.01' }],
    ['path', { d: 'M12 14h.01' }],
    ['path', { d: 'M8 14h.01' }],
    ['path', { d: 'M12 18h.01' }],
    ['path', { d: 'M8 18h.01' }],
  ],
  // lucide refresh-cw
  refresh: [
    ['path', { d: 'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' }, 'blue'],
    ['path', { d: 'M21 3v5h-5' }, 'blue'],
    ['path', { d: 'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' }, 'blue'],
    ['path', { d: 'M8 16H3v5' }, 'blue'],
  ],
  // lucide repeat
  iterate: [
    ['path', { d: 'm17 2 4 4-4 4' }, 'blue'],
    ['path', { d: 'M3 11v-1a4 4 0 0 1 4-4h14' }, 'blue'],
    ['path', { d: 'm7 22-4-4 4-4' }, 'blue'],
    ['path', { d: 'M21 13v1a4 4 0 0 1-4 4H3' }, 'blue'],
  ],
  // lucide arrow-down-a-z
  'sort-asc': [
    ['path', { d: 'm3 16 4 4 4-4' }, 'blue'],
    ['path', { d: 'M7 20V4' }, 'blue'],
    ['path', { d: 'M20 8h-5' }],
    ['path', { d: 'M15 10V6.5a2.5 2.5 0 0 1 5 0V10' }],
    ['path', { d: 'M15 14h5l-5 6h5' }],
  ],
  // lucide arrow-down-z-a
  'sort-desc': [
    ['path', { d: 'm3 16 4 4 4-4' }, 'blue'],
    ['path', { d: 'M7 4v16' }, 'blue'],
    ['path', { d: 'M15 4h5l-5 6h5' }],
    ['path', { d: 'M15 20v-3.5a2.5 2.5 0 0 1 5 0V20' }],
    ['path', { d: 'M20 18h-5' }],
  ],
  // lucide arrow-down-wide-narrow
  sort: [
    ['path', { d: 'm3 16 4 4 4-4' }, 'blue'],
    ['path', { d: 'M7 20V4' }, 'blue'],
    ['path', { d: 'M11 4h10' }],
    ['path', { d: 'M11 8h7' }],
    ['path', { d: 'M11 12h4' }],
  ],
  // lucide copy-x
  'remove-duplicates': [
    ['rect', { width: '14', height: '14', x: '8', y: '8', rx: '2', ry: '2' }, 'ink', 'paper'],
    ['line', { x1: '12', x2: '18', y1: '12', y2: '18' }, 'red'],
    ['line', { x1: '12', x2: '18', y1: '18', y2: '12' }, 'red'],
    ['path', { d: 'M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' }, 'muted'],
  ],
  // lucide square-split-horizontal
  'text-to-columns': [
    ['path', { d: 'M8 19H5c-1 0-2-1-2-2V7c0-1 1-2 2-2h3' }],
    ['path', { d: 'M16 5h3c1 0 2 1 2 2v10c0 1-1 2-2 2h-3' }],
    ['line', { x1: '12', x2: '12', y1: '4', y2: '20' }, 'blue'],
  ],
  // lucide combine
  consolidate: [
    ['rect', { x: '14', y: '14', width: '7', height: '7', rx: '1' }, 'ink', 'paper'],
    ['rect', { x: '3', y: '3', width: '7', height: '7', rx: '1' }, 'ink', 'paper'],
    ['path', { d: 'M14 3a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1' }, 'muted'],
    ['path', { d: 'M19 3a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1' }, 'muted'],
    ['path', { d: 'm7 15 3 3' }, 'blue'],
    ['path', { d: 'm7 21 3-3H5a2 2 0 0 1-2-2v-2' }, 'blue'],
  ],
  // lucide funnel
  filter: [
    [
      'path',
      {
        d: 'M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z',
      },
      'ink',
      'paper',
    ],
  ],
  // lucide funnel-plus
  'filter-advanced': [
    [
      'path',
      {
        d: 'M13.354 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14v6a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341l1.218-1.348',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M16 6h6' }, 'green'],
    ['path', { d: 'M19 3v6' }, 'green'],
  ],
  // lucide search
  find: [
    ['circle', { cx: '11', cy: '11', r: '8' }, 'ink', 'blue-tint'],
    ['path', { d: 'm21 21-4.34-4.34' }],
  ],
  // lucide replace
  replace: [
    ['rect', { x: '3', y: '14', width: '7', height: '7', rx: '1' }, 'ink', 'paper'],
    ['path', { d: 'M14 4a1 1 0 0 1 1-1' }],
    ['path', { d: 'M15 10a1 1 0 0 1-1-1' }],
    ['path', { d: 'M21 4a1 1 0 0 0-1-1' }],
    ['path', { d: 'M21 9a1 1 0 0 1-1 1' }],
    ['path', { d: 'm3 7 3 3 3-3' }, 'blue'],
    ['path', { d: 'M6 10V5a2 2 0 0 1 2-2h2' }, 'blue'],
  ],
  // lucide square-mouse-pointer
  'select-special': [
    [
      'path',
      {
        d: 'M12.034 12.681a.498.498 0 0 1 .647-.647l9 3.5a.5.5 0 0 1-.033.943l-3.444 1.068a1 1 0 0 0-.66.66l-1.067 3.443a.5.5 0 0 1-.943.033z',
      },
      'blue',
      'blue-tint',
    ],
    ['path', { d: 'M21 11V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6' }],
  ],
  // lucide tag
  names: [
    [
      'path',
      {
        d: 'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z',
      },
      'yellow-line',
      'yellow-tint',
    ],
    ['circle', { cx: '7.5', cy: '7.5', r: '.5', fill: 'currentColor' }, 'yellow-line'],
  ],
  // lucide list-plus
  'custom-lists': [
    ['path', { d: 'M16 5H3' }],
    ['path', { d: 'M11 12H3' }],
    ['path', { d: 'M16 19H3' }],
    ['path', { d: 'M18 9v6' }, 'green'],
    ['path', { d: 'M21 12h-6' }, 'green'],
  ],
  // lucide group
  group: [
    ['rect', { width: '7', height: '5', x: '7', y: '7', rx: '1' }, 'blue', 'blue-tint'],
    ['rect', { width: '7', height: '5', x: '10', y: '12', rx: '1' }, 'blue', 'blue-tint'],
    ['path', { d: 'M3 7V5c0-1.1.9-2 2-2h2' }, 'muted'],
    ['path', { d: 'M17 3h2c1.1 0 2 .9 2 2v2' }, 'muted'],
    ['path', { d: 'M21 17v2c0 1.1-.9 2-2 2h-2' }, 'muted'],
    ['path', { d: 'M7 21H5c-1.1 0-2-.9-2-2v-2' }, 'muted'],
  ],
  // lucide ungroup
  ungroup: [
    ['rect', { width: '8', height: '6', x: '5', y: '4', rx: '1' }, 'blue', 'blue-tint'],
    ['rect', { width: '8', height: '6', x: '11', y: '14', rx: '1' }, 'blue', 'blue-tint'],
  ],
  // lucide square-plus
  'show-detail': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M8 12h8' }, 'blue'],
    ['path', { d: 'M12 8v8' }, 'blue'],
  ],
  // lucide square-minus
  'hide-detail': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M8 12h8' }, 'blue'],
  ],
  // lucide list-tree
  'auto-outline': [
    ['path', { d: 'M8 5h13' }],
    ['path', { d: 'M13 12h8' }],
    ['path', { d: 'M13 19h8' }],
    ['path', { d: 'M3 10a2 2 0 0 0 2 2h3' }, 'blue'],
    ['path', { d: 'M3 5v12a2 2 0 0 0 2 2h3' }, 'blue'],
  ],
  // own
  subtotal: [
    ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '3', y: '15.5', width: '18', height: '5.5' }, 'ink', 'blue-tint'],
    ['path', { d: 'M3 8h18M3 12.5h18' }, 'muted'],
    ['path', { d: 'M9 17h5m-5 0 2.2 1.25L9 19.5h5' }, 'blue'],
  ],
  // own
  'conditional-format': [
    ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '5.5', y: '4.75', width: '12', height: '2.5', rx: '.5' }, 'none', 'red'],
    ['rect', { x: '5.5', y: '10.75', width: '8', height: '2.5', rx: '.5' }, 'none', 'yellow'],
    ['rect', { x: '5.5', y: '16.75', width: '4.5', height: '2.5', rx: '.5' }, 'none', 'green'],
    ['path', { d: 'M3 9h18M3 15h18' }, 'muted'],
  ],
  // lucide list-checks
  'data-validation': [
    ['path', { d: 'M13 5h8' }],
    ['path', { d: 'M13 12h8' }],
    ['path', { d: 'M13 19h8' }],
    ['path', { d: 'm3 17 2 2 4-4' }, 'green'],
    ['path', { d: 'm3 7 2 2 4-4' }, 'green'],
  ],
  // lucide flask-conical
  'what-if': [
    [
      'path',
      {
        d: 'M14 2v6a2 2 0 0 0 .245.96l5.51 10.08A2 2 0 0 1 18 22H6a2 2 0 0 1-1.755-2.96l5.51-10.08A2 2 0 0 0 10 8V2',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M6.453 15h11.094' }, 'blue'],
    ['path', { d: 'M8.5 2h7' }],
  ],
  // lucide target
  'goal-seek': [
    ['circle', { cx: '12', cy: '12', r: '10' }, 'ink', 'paper'],
    ['circle', { cx: '12', cy: '12', r: '2' }, 'red', 'red'],
    ['circle', { cx: '12', cy: '12', r: '6' }, 'red'],
  ],
  // lucide puzzle
  solver: [
    [
      'path',
      {
        d: 'M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z',
      },
      'blue',
      'blue-tint',
    ],
  ],
  // lucide layers
  scenarios: [
    [
      'path',
      {
        d: 'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
      },
      'blue',
      'blue-tint',
    ],
    [
      'path',
      { d: 'M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12' },
      'blue',
    ],
    [
      'path',
      { d: 'M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17' },
      'blue',
    ],
  ],
  // lucide sheet
  'data-table': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2', ry: '2' }, 'ink', 'paper'],
    ['line', { x1: '3', x2: '21', y1: '9', y2: '9' }, 'green'],
    ['line', { x1: '3', x2: '21', y1: '15', y2: '15' }, 'muted'],
    ['line', { x1: '9', x2: '9', y1: '9', y2: '21' }, 'muted'],
    ['line', { x1: '15', x2: '15', y1: '9', y2: '21' }, 'muted'],
  ],
  // own
  forecast: [
    ['path', { d: 'M3 3v17a1 1 0 0 0 1 1h17' }],
    ['path', { d: 'm6 16 3-3.5 3 2 3-4.5' }, 'blue'],
    ['path', { d: 'm15 10 5-4', 'stroke-dasharray': '1.5 2.2' }, 'orange'],
  ],
  // own
  'protect-sheet': [
    ['rect', { x: '2.5', y: '2.5', width: '15', height: '15', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '12.5', y: '14', width: '9', height: '7.5', rx: '1' }, 'yellow-line', 'yellow'],
    ['path', { d: 'M2.5 7.5h15M7.5 2.5v15' }, 'muted'],
    ['path', { d: 'M14.5 14v-2a2.5 2.5 0 0 1 5 0v2' }, 'yellow-line'],
  ],
  // lucide book-lock
  'protect-workbook': [
    ['path', { d: 'M20 15v6a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20' }, 'ink', 'paper'],
    ['path', { d: 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H10' }, 'ink', 'paper'],
    ['rect', { x: '12', y: '6', width: '8', height: '5', rx: '1' }, 'yellow-line', 'yellow'],
    ['path', { d: 'M18 6V4a2 2 0 1 0-4 0v2' }, 'yellow-line'],
  ],
  // lucide lock-open
  'allowed-ranges': [
    [
      'rect',
      { width: '18', height: '11', x: '3', y: '11', rx: '2', ry: '2' },
      'yellow-line',
      'yellow',
    ],
    ['path', { d: 'M7 11V7a5 5 0 0 1 9.9-1' }, 'yellow-line'],
  ],
  // lucide layout-template
  templates: [
    ['rect', { width: '18', height: '7', x: '3', y: '3', rx: '1' }, 'blue', 'blue-tint'],
    ['rect', { width: '9', height: '7', x: '3', y: '14', rx: '1' }, 'ink', 'paper'],
    ['rect', { width: '5', height: '7', x: '16', y: '14', rx: '1' }, 'ink', 'paper'],
  ],
  // lucide file-down
  'download-pdf': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M12 18v-6' }, 'red'],
    ['path', { d: 'm9 15 3 3 3-3' }, 'red'],
  ],
  // lucide file-code
  'export-html': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'ink', 'paper'],
    ['path', { d: 'M10 12.5 8 15l2 2.5' }, 'orange'],
    ['path', { d: 'm14 12.5 2 2.5-2 2.5' }, 'orange'],
  ],
  // lucide undo-2
  undo: [
    ['path', { d: 'M9 14 4 9l5-5' }],
    ['path', { d: 'M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11' }],
  ],
  // lucide redo-2
  redo: [
    ['path', { d: 'm15 14 5-5-5-5' }],
    ['path', { d: 'M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13' }],
  ],
  // lucide superscript
  superscript: [
    ['path', { d: 'm4 19 8-8' }],
    ['path', { d: 'm12 19-8-8' }],
    [
      'path',
      {
        d: 'M20 12h-4c0-1.5.442-2 1.5-2.5S20 8.334 20 7.002c0-.472-.17-.93-.484-1.29a2.105 2.105 0 0 0-2.617-.436c-.42.239-.738.614-.899 1.06',
      },
      'blue',
    ],
  ],
  // lucide subscript
  subscript: [
    ['path', { d: 'm4 5 8 8' }],
    ['path', { d: 'm12 5-8 8' }],
    [
      'path',
      {
        d: 'M20 19h-4c0-1.5.44-2 1.5-2.5S20 15.33 20 14c0-.47-.17-.93-.48-1.29a2.11 2.11 0 0 0-2.62-.44c-.42.24-.74.62-.9 1.07',
      },
      'blue',
    ],
  ],
  // lucide highlighter
  highlight: [
    ['path', { d: 'm9 11-6 6v3h9l3-3' }, 'yellow-line', 'yellow'],
    ['path', { d: 'm22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4' }],
  ],
  // lucide pen-line; the line is a bar in the pen's colour (`current`)
  pen: [
    ['rect', { x: '13', y: '19.5', width: '8', height: '3' }, 'none', 'fill-current'],
    [
      'path',
      {
        d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
      },
      'ink',
      'blue-tint',
    ],
  ],
  // lucide square-pen
  drawing: [
    ['path', { d: 'M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }, 'ink', 'paper'],
    [
      'path',
      {
        d: 'M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z',
      },
      'blue',
    ],
  ],
  // lucide remove-formatting
  'clear-formatting': [
    ['path', { d: 'M4 7V4h16v3' }],
    ['path', { d: 'M5 20h6' }],
    ['path', { d: 'M13 4 8 20' }],
    ['path', { d: 'm15 15 5 5' }, 'red'],
    ['path', { d: 'm20 15-5 5' }, 'red'],
  ],
  // lucide ellipsis
  more: [
    ['circle', { cx: '12', cy: '12', r: '1' }],
    ['circle', { cx: '19', cy: '12', r: '1' }],
    ['circle', { cx: '5', cy: '12', r: '1' }],
  ],
  // lucide text-align-justify
  'align-justify': [
    ['path', { d: 'M3 5h18' }],
    ['path', { d: 'M3 12h18' }],
    ['path', { d: 'M3 19h18' }],
  ],
  // lucide list
  bullets: [
    ['path', { d: 'M3 5h.01' }, 'blue'],
    ['path', { d: 'M3 12h.01' }, 'blue'],
    ['path', { d: 'M3 19h.01' }, 'blue'],
    ['path', { d: 'M8 5h13' }],
    ['path', { d: 'M8 12h13' }],
    ['path', { d: 'M8 19h13' }],
  ],
  // lucide list-ordered
  numbering: [
    ['path', { d: 'M11 5h10' }],
    ['path', { d: 'M11 12h10' }],
    ['path', { d: 'M11 19h10' }],
    ['path', { d: 'M4 4h1v5' }, 'blue'],
    ['path', { d: 'M4 9h2' }, 'blue'],
    ['path', { d: 'M6.5 20H3.4c0-1 2.6-1.925 2.6-3.5a1.5 1.5 0 0 0-2.6-1.02' }, 'blue'],
  ],
  // lucide list-indent-decrease
  'indent-decrease': [
    ['path', { d: 'M21 5H11' }],
    ['path', { d: 'M21 12H11' }],
    ['path', { d: 'M21 19H11' }],
    ['path', { d: 'm7 8-4 4 4 4' }, 'blue'],
  ],
  // lucide list-indent-increase
  'indent-increase': [
    ['path', { d: 'M21 5H11' }],
    ['path', { d: 'M21 12H11' }],
    ['path', { d: 'M21 19H11' }],
    ['path', { d: 'm3 8 4 4-4 4' }, 'blue'],
  ],
  // lucide pilcrow
  'paragraph-marks': [
    ['path', { d: 'M13 4v16' }],
    ['path', { d: 'M17 4v16' }],
    ['path', { d: 'M19 4H9.5a4.5 4.5 0 0 0 0 9H13' }],
  ],
  // lucide plus
  add: [
    ['path', { d: 'M5 12h14' }, 'green'],
    ['path', { d: 'M12 5v14' }, 'green'],
  ],
  // lucide trash-2
  delete: [
    ['path', { d: 'M10 11v6' }, 'red'],
    ['path', { d: 'M14 11v6' }, 'red'],
    ['path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }, 'red'],
    ['path', { d: 'M3 6h18' }, 'red'],
    ['path', { d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }, 'red'],
  ],
  // lucide mouse-pointer-2
  select: [
    [
      'path',
      {
        d: 'M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z',
      },
      'ink',
      'paper',
    ],
  ],
  // own
  'page-break': [
    ['path', { d: 'M5 9.5V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v5.5' }, 'ink', 'paper'],
    ['path', { d: 'M5 14.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5.5' }, 'ink', 'paper'],
    ['path', { d: 'M2 12h20', 'stroke-dasharray': '2 2' }, 'blue'],
  ],
  // lucide quote
  quote: [
    [
      'path',
      {
        d: 'M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z',
      },
      'blue',
      'blue-tint',
    ],
    [
      'path',
      {
        d: 'M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z',
      },
      'blue',
      'blue-tint',
    ],
  ],
  // lucide separator-horizontal
  divider: [
    ['path', { d: 'm16 16-4 4-4-4' }, 'muted'],
    ['path', { d: 'M3 12h18' }],
    ['path', { d: 'm8 8 4-4 4 4' }, 'muted'],
  ],
  // lucide table-of-contents
  'table-of-contents': [
    ['path', { d: 'M16 5H3' }],
    ['path', { d: 'M16 12H3' }],
    ['path', { d: 'M16 19H3' }],
    ['path', { d: 'M21 5h.01' }, 'blue'],
    ['path', { d: 'M21 12h.01' }, 'blue'],
    ['path', { d: 'M21 19h.01' }, 'blue'],
  ],
  // lucide panel-top
  header: [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M3 9h18' }, 'blue'],
  ],
  // lucide panel-bottom
  footer: [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M3 15h18' }, 'blue'],
  ],
  // lucide hash
  'page-number': [
    ['line', { x1: '4', x2: '20', y1: '9', y2: '9' }, 'blue'],
    ['line', { x1: '4', x2: '20', y1: '15', y2: '15' }, 'blue'],
    ['line', { x1: '10', x2: '8', y1: '3', y2: '21' }, 'blue'],
    ['line', { x1: '16', x2: '14', y1: '3', y2: '21' }, 'blue'],
  ],
  // lucide files
  'page-count': [
    ['path', { d: 'M15 2h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8' }],
    ['path', { d: 'M16.706 2.706A2.4 2.4 0 0 0 15 2v5a1 1 0 0 0 1 1h5a2.4 2.4 0 0 0-.706-1.706z' }],
    ['path', { d: 'M5 7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 1.732-1' }],
  ],
  // lucide calendar
  date: [
    ['rect', { width: '18', height: '18', x: '3', y: '4', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M8 2v4' }],
    ['path', { d: 'M16 2v4' }],
    ['path', { d: 'M3 10h18' }, 'blue'],
  ],
  // lucide x
  close: [
    ['path', { d: 'M18 6 6 18' }, 'red'],
    ['path', { d: 'm6 6 12 12' }, 'red'],
  ],
  // lucide between-horizontal-end
  'insert-row': [
    ['rect', { width: '13', height: '7', x: '3', y: '3', rx: '1' }, 'ink', 'paper'],
    ['rect', { width: '13', height: '7', x: '3', y: '14', rx: '1' }, 'ink', 'paper'],
    ['path', { d: 'm22 15-3-3 3-3' }, 'green'],
  ],
  // lucide between-vertical-end
  'insert-column': [
    ['rect', { width: '7', height: '13', x: '3', y: '3', rx: '1' }, 'ink', 'paper'],
    ['rect', { width: '7', height: '13', x: '14', y: '3', rx: '1' }, 'ink', 'paper'],
    ['path', { d: 'm9 22 3-3 3 3' }, 'green'],
  ],
  // own
  'delete-row': [
    ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '3', y: '9', width: '18', height: '6' }, 'ink', 'red-tint'],
    ['path', { d: 'm14 10.5 3 3m0-3-3 3' }, 'red'],
  ],
  // own
  'delete-column': [
    ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '1.5' }, 'ink', 'paper'],
    ['rect', { x: '9', y: '3', width: '6', height: '18' }, 'ink', 'red-tint'],
    ['path', { d: 'm10.5 14 3 3m0-3-3 3' }, 'red'],
  ],
  // lucide rows-3
  'repeat-header-row': [
    ['rect', { width: '18', height: '18', x: '3', y: '3', rx: '2' }, 'ink', 'paper'],
    ['path', { d: 'M21 9H3' }, 'blue'],
    ['path', { d: 'M21 15H3' }, 'muted'],
  ],
  // lucide crop
  crop: [
    ['path', { d: 'M6 2v14a2 2 0 0 0 2 2h14' }],
    ['path', { d: 'M18 22V8a2 2 0 0 0-2-2H2' }],
  ],
  // lucide image-minus
  'delete-picture': [
    ['path', { d: 'M21 9v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7' }, 'ink', 'paper'],
    ['circle', { cx: '9', cy: '9', r: '2' }, 'yellow-line', 'yellow'],
    ['line', { x1: '16', x2: '22', y1: '5', y2: '5' }, 'red'],
    ['path', { d: 'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21' }, 'green'],
  ],
  // lucide zoom-out
  'zoom-out': [
    ['circle', { cx: '11', cy: '11', r: '8' }, 'ink', 'blue-tint'],
    ['line', { x1: '21', x2: '16.65', y1: '21', y2: '16.65' }],
    ['line', { x1: '8', x2: '14', y1: '11', y2: '11' }, 'blue'],
  ],
  // lucide zoom-in
  'zoom-in': [
    ['circle', { cx: '11', cy: '11', r: '8' }, 'ink', 'blue-tint'],
    ['line', { x1: '21', x2: '16.65', y1: '21', y2: '16.65' }],
    ['line', { x1: '11', x2: '11', y1: '8', y2: '14' }, 'blue'],
    ['line', { x1: '8', x2: '14', y1: '11', y2: '11' }, 'blue'],
  ],
  // lucide whole-word
  'word-count': [
    ['circle', { cx: '7', cy: '12', r: '3' }],
    ['path', { d: 'M10 9v6' }],
    ['circle', { cx: '17', cy: '12', r: '3' }],
    ['path', { d: 'M14 7v8' }],
    ['path', { d: 'M22 17v1c0 .5-.5 1-1 1H3c-.5 0-1-.5-1-1v-1' }],
  ],
  // lucide percent
  'zoom-reset': [
    ['line', { x1: '19', x2: '5', y1: '5', y2: '19' }],
    ['circle', { cx: '6.5', cy: '6.5', r: '2.5' }],
    ['circle', { cx: '17.5', cy: '17.5', r: '2.5' }],
  ],
  // lucide maximize-2
  'fit-page': [
    ['path', { d: 'M15 3h6v6' }, 'blue'],
    ['path', { d: 'm21 3-7 7' }, 'blue'],
    ['path', { d: 'm3 21 7-7' }, 'blue'],
    ['path', { d: 'M9 21H3v-6' }, 'blue'],
  ],
  // lucide rectangle-vertical
  'orientation-portrait': [
    ['rect', { width: '12', height: '20', x: '6', y: '2', rx: '2' }, 'ink', 'paper'],
  ],
  // lucide rectangle-horizontal
  'orientation-landscape': [
    ['rect', { width: '20', height: '12', x: '2', y: '6', rx: '2' }, 'ink', 'paper'],
  ],
  // lucide files
  'text-documents': [
    ['path', { d: 'M15 2h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8' }, 'ink', 'paper'],
    ['path', { d: 'M16.706 2.706A2.4 2.4 0 0 0 15 2v5a1 1 0 0 0 1 1h5a2.4 2.4 0 0 0-.706-1.706z' }],
    ['path', { d: 'M5 7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 1.732-1' }],
  ],
  // lucide signature
  signature: [
    [
      'path',
      {
        d: 'm21 17-2.156-1.868A.5.5 0 0 0 18 15.5v.5a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1c0-2.545-3.991-3.97-8.5-4a1 1 0 0 0 0 5c4.153 0 4.745-11.295 5.708-13.5a2.5 2.5 0 1 1 3.31 3.284',
      },
      'blue',
    ],
    ['path', { d: 'M3 21h18' }],
  ],
  // lucide library
  library: [
    ['path', { d: 'm16 6 4 14' }, 'orange'],
    ['path', { d: 'M12 6v14' }],
    ['path', { d: 'M8 8v12' }],
    ['path', { d: 'M4 4v16' }],
  ],
  // lucide braces
  'merge-fields': [
    ['path', { d: 'M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1' }, 'blue'],
    [
      'path',
      { d: 'M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1' },
      'blue',
    ],
  ],
  // lucide file-warning
  'import-report': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'ink',
      'paper',
    ],
    ['path', { d: 'M12 9v4' }, 'orange'],
    ['path', { d: 'M12 17h.01' }, 'orange'],
  ],
  // lucide clock
  time: [
    ['circle', { cx: '12', cy: '12', r: '10' }, 'ink', 'paper'],
    ['path', { d: 'M12 6v6l4 2' }, 'blue'],
  ],
  // lucide decimals-arrow-right
  'number-format': [
    ['path', { d: 'M10 18h10' }, 'blue'],
    ['path', { d: 'm17 21 3-3-3-3' }, 'blue'],
    ['path', { d: 'M3 11h.01' }],
    ['rect', { x: '15', y: '3', width: '5', height: '8', rx: '2.5' }],
    ['rect', { x: '6', y: '3', width: '5', height: '8', rx: '2.5' }],
  ],
  // lucide euro
  currency: [
    ['path', { d: 'M4 10h12' }, 'green'],
    ['path', { d: 'M4 14h9' }, 'green'],
    [
      'path',
      { d: 'M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2' },
      'green',
    ],
  ],
  // lucide percent
  percent: [
    ['line', { x1: '19', x2: '5', y1: '5', y2: '19' }],
    ['circle', { cx: '6.5', cy: '6.5', r: '2.5' }, 'blue'],
    ['circle', { cx: '17.5', cy: '17.5', r: '2.5' }, 'blue'],
  ],
  // lucide circle-slash-2
  'number-format-general': [
    ['circle', { cx: '12', cy: '12', r: '10' }],
    ['path', { d: 'M22 2 2 22' }, 'red'],
  ],
  // lucide file-spreadsheet, ribbon header
  'document-calc': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'calc',
      'calc',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'calc-on'],
    ['path', { d: 'M8 13h2' }, 'calc-on'],
    ['path', { d: 'M14 13h2' }, 'calc-on'],
    ['path', { d: 'M8 17h2' }, 'calc-on'],
    ['path', { d: 'M14 17h2' }, 'calc-on'],
  ],
  // lucide file-text, ribbon header
  'document-write': [
    [
      'path',
      {
        d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
      },
      'write',
      'write',
    ],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }, 'write-on'],
    ['path', { d: 'M10 9H8' }, 'write-on'],
    ['path', { d: 'M16 13H8' }, 'write-on'],
    ['path', { d: 'M16 17H8' }, 'write-on'],
  ],
} as const satisfies Record<string, readonly NxOfficeIconNode[]>;

/** A formatting letter: the glyph, and the bar or caret that says what it does. */
export interface NxOfficeLetter {
  readonly text: string;
  readonly weight?: 'bold';
  readonly size?: 'small';
  /** `color` shows the colour a font-colour button stands for (`current`). */
  readonly bar?: 'underline' | 'strike' | 'color';
  readonly caret?: 'up' | 'down';
}

export const NX_OFFICE_LETTERS = {
  bold: { text: 'B', weight: 'bold' },
  underline: { text: 'U', bar: 'underline' },
  strikethrough: { text: 'S', bar: 'strike' },
  'font-color': { text: 'A', bar: 'color' },
  'change-case': { text: 'Aa' },
  'font-grow': { text: 'A', caret: 'up' },
  'font-shrink': { text: 'A', caret: 'down', size: 'small' },
} as const satisfies Record<string, NxOfficeLetter>;

export type NxOfficeIconName = keyof typeof NX_OFFICE_ICONS | keyof typeof NX_OFFICE_LETTERS;

/** Every icon of the set, drawings and letters. */
export const NX_OFFICE_ICON_NAMES = [
  ...Object.keys(NX_OFFICE_ICONS),
  ...Object.keys(NX_OFFICE_LETTERS),
] as NxOfficeIconName[];
